import { AtlBrowserStorageSession } from '@atlassian/browser-storage-controls';

import { isBrowser } from '../is-browser';

/**
 * Browser-session-storage-backed store tracking which nudges have already been shown.
 * Entries expire after 7 days; capped at 500 to prevent unbounded growth.
 *
 * Two separate sessionStorage keys are used:
 *   - `rovo-button-nudges-suppression` (STRICTLY_NECESSARY): tracks nudges suppressed via
 *     explicit user interaction (click, dismiss, auto-dismiss timer). Written with
 *     `setStrictlyNecessaryItem` so it is always available regardless of consent.
 *   - `rovo-button-nudges-ots` (Functional): tracks nudges suppressed via OTS
 *     (opportunity-to-see — 5 second passive view). Written with `setItem` so it is
 *     consent-gated.
 */

const STORAGE_KEY = 'rovo-button-nudges-suppression';
const OTS_STORAGE_KEY = 'rovo-button-nudges-ots';

// Bounded growth constraints.
const MAX_ENTRIES = 500;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Map of suppression key → expiresAt (epoch ms). */
export type SuppressionMap = Map<string, number>;

/**
 * Drop expired entries and, if still over the cap, drop the oldest by expiry.
 * Returns a new map (pure).
 */
const pruneEntries = (map: SuppressionMap, now: number = Date.now()): SuppressionMap => {
	// 1. Drop expired entries.
	const fresh: SuppressionMap = new Map();
	for (const [key, expiresAt] of map) {
		if (expiresAt > now) {
			fresh.set(key, expiresAt);
		}
	}
	// 2. If still over cap, drop oldest entries by expiry.
	if (fresh.size <= MAX_ENTRIES) {
		return fresh;
	}
	const sorted = Array.from(fresh.entries()).sort((a, b) => a[1] - b[1]);
	return new Map(sorted.slice(sorted.length - MAX_ENTRIES));
};

/** Parse a sessionStorage value as a SuppressionMap. Returns an empty Map on miss/corrupt. */
const parseFromStorage = (storageKey: string): SuppressionMap => {
	try {
		const raw = AtlBrowserStorageSession.getItem(storageKey);
		if (!raw) {
			return new Map();
		}
		const arr: unknown = JSON.parse(raw);
		if (
			Array.isArray(arr) &&
			arr.every(
				(e): e is [string, number] =>
					Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'number',
			)
		) {
			return new Map(arr);
		}
	} catch {
		// Corrupt JSON — start fresh.
	}
	return new Map();
};

// ---------------------------------------------------------------------------
// Suppression store — STRICTLY_NECESSARY (click, dismiss, auto-dismiss timer)
// ---------------------------------------------------------------------------

/** In-memory cache for the suppression store. Prevents JSON.parse on every render. */
let memoryCache: SuppressionMap | null = null;

/** Best-effort write of a suppression map to sessionStorage. Memory cache is always source of truth. */
const writeToStorage = (map: SuppressionMap): void => {
	try {
		AtlBrowserStorageSession.setStrictlyNecessaryItem(
			STORAGE_KEY,
			JSON.stringify(Array.from(map.entries())),
		);
	} catch {
		// Best-effort. Memory cache is always source of truth.
	}
};

/**
 * Read the suppression map.
 * - SSR-safe: returns empty Map on the server.
 * - Memo-cached: only parses sessionStorage on first call per session.
 * - Read-only: never writes back to sessionStorage. Writes happen only in writeSuppression.
 */
export const readSuppressionMap = (): SuppressionMap => {
	if (!isBrowser()) {
		return new Map();
	}
	if (memoryCache) {
		// Defensive copy so callers cannot mutate the cache.
		return new Map(memoryCache);
	}
	const parsed = parseFromStorage(STORAGE_KEY);
	memoryCache = parsed;
	return new Map(parsed);
};

/**
 * Records a suppression entry for the given key. Prunes expired/excess entries on every write.
 * Note: sessionStorage is per-tab — a user with multiple tabs open may see the same nudge once per tab.
 */
export const writeSuppression = (suppressionKey: string): void => {
	if (!isBrowser()) {
		return;
	}
	const current = readSuppressionMap();
	// Guard against silently refreshing TTL on an already-suppressed key.
	if (current.has(suppressionKey)) {
		return;
	}
	const next = new Map(current);
	next.set(suppressionKey, Date.now() + MAX_AGE_MS);
	const pruned = pruneEntries(next);
	memoryCache = pruned;
	writeToStorage(pruned);
};

/** Returns true if the suppression key has an active, non-expired entry. */
export const isKeySuppressed = (suppressionKey: string): boolean => {
	const expiresAt = readSuppressionMap().get(suppressionKey);
	return expiresAt !== undefined && expiresAt > Date.now();
};

/** Remove a single suppression entry from both stores. Used by the debug API. */
export const clearSuppression = (suppressionKey: string): void => {
	if (!isBrowser()) {
		return;
	}
	const next = new Map(readSuppressionMap());
	next.delete(suppressionKey);
	memoryCache = next;
	writeToStorage(next);

	const nextOts = new Map(readOtsMap());
	nextOts.delete(suppressionKey);
	otsMemoryCache = nextOts;
	writeOtsToStorage(nextOts);
};

/** Remove all suppression entries from both stores. Used by the debug API. */
export const clearAllSuppression = (): void => {
	if (!isBrowser()) {
		return;
	}
	memoryCache = new Map();
	otsMemoryCache = new Map();
	try {
		AtlBrowserStorageSession.removeItem(STORAGE_KEY);
	} catch {
		// Best-effort.
	}
	try {
		AtlBrowserStorageSession.removeItem(OTS_STORAGE_KEY);
	} catch {
		// Best-effort.
	}
};

// ---------------------------------------------------------------------------
// OTS store — Functional / consent-gated (5-second passive view)
// ---------------------------------------------------------------------------

/** In-memory cache for the OTS store. Prevents JSON.parse on every render. */
let otsMemoryCache: SuppressionMap | null = null;

/** Best-effort write of the OTS map to sessionStorage. Memory cache is always source of truth. */
const writeOtsToStorage = (map: SuppressionMap): void => {
	try {
		AtlBrowserStorageSession.setItem(OTS_STORAGE_KEY, JSON.stringify(Array.from(map.entries())));
	} catch {
		// Best-effort. Memory cache is always source of truth.
	}
};

/**
 * Read the OTS map.
 * - SSR-safe: returns empty Map on the server.
 * - Memo-cached: only parses sessionStorage on first call per session.
 */
export const readOtsMap = (): SuppressionMap => {
	if (!isBrowser()) {
		return new Map();
	}
	if (otsMemoryCache) {
		// Defensive copy so callers cannot mutate the cache.
		return new Map(otsMemoryCache);
	}
	const parsed = parseFromStorage(OTS_STORAGE_KEY);
	otsMemoryCache = parsed;
	return new Map(parsed);
};

/**
 * Records an OTS entry for the given key (nudge was passively viewed for 5 seconds).
 * Uses the Functional-category sessionStorage key `rovo-button-nudges-ots`.
 */
export const writeOtsSuppression = (suppressionKey: string): void => {
	if (!isBrowser()) {
		return;
	}
	const current = readOtsMap();
	// Guard against silently refreshing TTL on an already-recorded key.
	if (current.has(suppressionKey)) {
		return;
	}
	const next = new Map(current);
	next.set(suppressionKey, Date.now() + MAX_AGE_MS);
	const pruned = pruneEntries(next);
	otsMemoryCache = pruned;
	writeOtsToStorage(pruned);
};

/** Returns true if the OTS key has an active, non-expired entry. */
export const isOtsKeySuppressed = (suppressionKey: string): boolean => {
	const expiresAt = readOtsMap().get(suppressionKey);
	return expiresAt !== undefined && expiresAt > Date.now();
};
